import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * One labelled control on a filter row.
 *
 * THE HEIGHT RULE, stated once so it stops being re-litigated per screen: filter-row
 * controls are AntD's default size, which `antdTheme.ts` sets to `controlHeight` (38px),
 * and any shadcn control joining them uses the same `--control-height`. Mixing AntD's
 * default with shadcn's is a one-pixel misalignment that reads as sloppiness across an
 * entire row and never shows up in a diff.
 */
export type FilterFieldProps = {
  label: string;
  children: ReactNode;
  className?: string;
  /**
   * ⚠️ [UX1 §7] `stacked` IS THE DEFAULT AND IS WHAT EVERY CONSUMER HAS ALWAYS BEEN — the
   * label ABOVE the control, on a filter ROW.
   *
   * `inline` puts label and control on ONE line, for a control that joins a HEADING row rather
   * than a filter row. `PageHeader`'s `actions` cluster is `items-center`, so a `flex-col`
   * field dropped into it sits a half-line high with its label floating above the heading —
   * which is what the dashboard's date range looked like, and what was reported as the fields
   * "rendering twice".
   *
   * ⚠️ IT IS DATA WITH A DEFAULT, NOT A CLASS PROP, AND IT IS RENDERED AS AN ATTRIBUTE. The
   * idiom `SectionDescriptor.surface`, `TwoColumnLayout.secondaryOpen` and
   * `OrgUnitPicker.defaultExpandAll` already establish: a new mode must not move the thirteen
   * consumers that did not ask for one. `data-filter-field` is emitted on BOTH branches
   * deliberately — jsdom resolves no cascade, so the attribute is the only thing a test can
   * see, and it is what lets the dashboard COUNT its range controls.
   */
  layout?: 'stacked' | 'inline';
};

export const FilterField = ({ label, children, className, layout = 'stacked' }: FilterFieldProps) => (
  <label
    data-filter-field={layout}
    className={cn('flex min-w-0', layout === 'inline' ? 'items-center gap-2' : 'flex-col gap-1', className)}
  >
    {/* `shrink-0` is a no-op in a column and required in a row, so it is unconditional. */}
    <span className="shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </label>
);
